import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import useAnimatedModal from '../../hooks/useAnimatedModal';
import useDialogA11y from '../../hooks/useDialogA11y';
import styles from './styles.module.css';

/**
 * Bottom-sheet (or fullscreen) modal with enter + exit animations.
 * Pass `open` from the parent; `onClose` fires after the close animation finishes.
 *
 * ── `trapFocus` DEFAULTS TO ON, AND THAT IS THE FIX ───────────────────────
 *
 * It used to default to OFF, with a comment reasoning that this component has
 * a great many callers and "quietly changing where focus lands in all of them
 * is a change nobody asked for". The result was five dialogs in this release
 * that a keyboard walked straight out of — including the checkout login gate,
 * where the digits of a phone number landed in the shipping field behind the
 * overlay, invisible, at the moment somebody was trying to buy something.
 *
 * Every one of those dialogs already renders `role="dialog" aria-modal="true"`.
 * That attribute is a PROMISE to assistive technology that the rest of the
 * document is unreachable. An opt-in trap means the promise is false unless
 * somebody remembered — and the whole point of a default is that it is what
 * happens when nobody remembered.
 *
 * A caller that genuinely wants a non-modal panel passes `trapFocus={false}`
 * and should drop `aria-modal` with it. Escape and the scroll lock are NOT
 * part of that opt-out: a sheet you cannot dismiss with a key is not a design
 * choice anybody has ever asked for.
 *
 * The behaviour itself lives in `useDialogA11y`, shared with the dialogs in
 * this app that are not `AnimatedModal`s — the checkout login gate and the
 * product media viewer — so all of them answer the keyboard the same way.
 */
const AnimatedModal = ({
  open,
  onClose,
  children,
  testId,
  ariaLabel,
  overlayClassName = '',
  sheetClassName = '',
  variant = 'sheet',
  closeOnOverlay = true,
  closeDurationMs = 280,
  trapFocus = true,
}) => {
  const { mounted, closing, requestClose } = useAnimatedModal(open, onClose, closeDurationMs);
  const overlayRef = useRef(null);

  /* The OVERLAY is the container, not the sheet: the overlay is the element
   * carrying `role="dialog"`, and "the background is inert" is a claim about
   * this element's siblings.
   *
   * `mounted && !closing` — the sheet stays in the document for its 280ms exit,
   * and a reader should not be held inside a sheet that is leaving. Focus goes
   * home, and the page comes back, the moment it stops being open. */
  useDialogA11y({
    active: mounted && !closing && trapFocus,
    containerRef: overlayRef,
    onEscape: requestClose,
  });

  /* Escape for the opted-out case only — when the trap is on, the shared hook
   * owns the key, and the topmost open dialog is the one that answers it.
   * Bound twice, one dialog would close two. */
  useEffect(() => {
    if (!mounted || trapFocus) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') requestClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [mounted, trapFocus, requestClose]);

  // Lock the page behind the modal so it can't scroll (or scroll-chain) while
  // the sheet is open — otherwise the body scrolls under the overlay.
  useEffect(() => {
    if (!mounted) return undefined;
    const { body, documentElement: html } = document;
    const prev = {
      bodyOverflow: body.style.overflow,
      bodyOverscroll: body.style.overscrollBehavior,
      htmlOverflow: html.style.overflow,
    };
    body.style.overflow = 'hidden';
    body.style.overscrollBehavior = 'contain';
    html.style.overflow = 'hidden';
    return () => {
      body.style.overflow = prev.bodyOverflow;
      body.style.overscrollBehavior = prev.bodyOverscroll;
      html.style.overflow = prev.htmlOverflow;
    };
  }, [mounted]);

  if (!mounted || typeof document === 'undefined') return null;

  const overlayClasses = [
    styles.overlay,
    variant === 'fullscreen' ? styles.overlayFullscreen : '',
    variant === 'search' ? styles.overlaySearch : '',
    closing ? styles.overlayClosing : '',
    overlayClassName,
  ].filter(Boolean).join(' ');

  const sheetClasses = [
    styles.sheet,
    variant === 'fullscreen' ? styles.sheetFullscreen : '',
    variant === 'search' ? styles.sheetSearch : '',
    closing ? styles.sheetClosing : '',
    sheetClassName,
  ].filter(Boolean).join(' ');

  return createPortal(
    <div
      className={overlayClasses}
      role="dialog"
      aria-modal="true"
      aria-label={ariaLabel}
      data-testid={testId}
      /* The exit animates, so "closing" and "closed" are different states and a
       * test that could only see the document could not tell them apart. */
      data-closing={closing ? 'true' : 'false'}
      ref={overlayRef}
      onClick={closeOnOverlay ? requestClose : undefined}
    >
      <div className={sheetClasses} onClick={(e) => e.stopPropagation()}>
        {typeof children === 'function' ? children({ requestClose, closing }) : children}
      </div>
    </div>,
    document.body,
  );
};

export default AnimatedModal;
